import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { API_BASE } from '../config'
import { useAdminAuth } from './AuthContext'

type QuickUser = { id: number; name: string; email: string; role: string; label: string }

export default function LoginPage() {
  const { user, loading, login, quickLogin } = useAdminAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [quickUsers, setQuickUsers] = useState<QuickUser[] | null>(null)
  const [quickPending, setQuickPending] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const st = await fetch(`${API_BASE}/api/admin/dev/quick-login/status`)
        const stData = (await st.json()) as { enabled?: boolean }
        if (!stData?.enabled) return
        const res = await fetch(`${API_BASE}/api/admin/dev/quick-login/users`)
        const data = (await res.json()) as { success?: boolean; users?: QuickUser[] }
        if (!cancelled && res.ok && data.success && data.users) setQuickUsers(data.users)
      } catch {
        /* quick login off */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="relative min-h-screen grid place-items-center overflow-hidden">
        <LoginAtmosphere />
        <p className="relative z-10 font-sans text-sm text-slate-500 animate-pulse-soft">Loading…</p>
      </div>
    )
  }

  if (user) return <Navigate to="/admin" replace />

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const err = await login(email, password)
    setBusy(false)
    if (err) setError(err)
    else navigate('/admin', { replace: true })
  }

  async function onQuick(u: QuickUser) {
    setError(null)
    setQuickPending(u.email)
    const err = await quickLogin(u.email)
    setQuickPending(null)
    if (err) setError(err)
    else navigate('/admin', { replace: true })
  }

  return (
    <div className="relative min-h-screen overflow-hidden flex flex-col">
      <LoginAtmosphere />

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-4 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[420px]"
        >
          <div className="mb-8 text-center">
            <Link to="/" className="inline-flex flex-col items-center group">
              <img
                src="/logo-light.png"
                alt="DigiSutra"
                className="h-12 sm:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
              />
            </Link>
            <div className="mx-auto mt-4 h-1 w-12 rounded-full bg-accent shadow-glow" />
            <p className="mt-3 text-sm sm:text-base text-slate-600 font-sans">
              Website content management
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="card-glass rounded-card p-6 sm:p-8"
          >
            <form onSubmit={onSubmit} className="space-y-5">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Email</span>
                <div className="relative">
                  <Mail
                    className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-primary/50"
                    aria-hidden
                  />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-2xl border border-primary/15 bg-white/80 px-3 py-3 pl-10 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm outline-none transition focus:border-primary/40 focus:ring-2 focus:ring-accent/40"
                    placeholder="you@digisutra.in"
                    autoComplete="username"
                  />
                </div>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Password</span>
                <div className="relative">
                  <Lock
                    className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-primary/50"
                    aria-hidden
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-2xl border border-primary/15 bg-white/80 px-3 py-3 pl-10 pr-11 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm outline-none transition focus:border-primary/40 focus:ring-2 focus:ring-accent/40"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 transition hover:text-primary"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </label>

              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100"
                  role="alert"
                >
                  {error}
                </motion.p>
              )}

              <button type="submit" disabled={busy} className="btn-primary w-full gap-2 disabled:opacity-60 disabled:hover:scale-100">
                {busy ? (
                  'Signing in…'
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </>
                )}
              </button>
            </form>

            {quickUsers && quickUsers.length > 0 && (
              <div className="mt-6 border-t border-primary/10 pt-5 space-y-2.5">
                <p className="text-[0.65rem] font-bold uppercase tracking-[0.16em] text-primary/70">
                  Quick login · testing
                </p>
                <div className="space-y-2">
                  {quickUsers.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      disabled={!!quickPending}
                      onClick={() => void onQuick(u)}
                      className="w-full rounded-2xl border border-primary/15 bg-primary/[0.04] px-3.5 py-2.5 text-left text-sm font-medium text-navy-900 transition hover:border-primary/30 hover:bg-primary/[0.08] disabled:opacity-60"
                    >
                      <span className="block">{quickPending === u.email ? 'Signing in…' : u.name}</span>
                      <span className="block text-xs font-normal text-slate-500">{u.role} · {u.email}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          <p className="mt-6 text-center text-xs text-slate-500">
            <Link to="/" className="font-medium text-primary hover:text-primary-light transition">
              ← Back to website
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  )
}

function LoginAtmosphere() {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(165deg, #fafbff 0%, #eef2ff 32%, #ffffff 68%, #f8fafc 100%)',
        }}
      />
      <div className="absolute -left-24 top-16 h-80 w-80 rounded-full bg-primary/20 blur-3xl animate-float" />
      <div
        className="absolute -right-20 bottom-24 h-72 w-72 rounded-full bg-accent/25 blur-3xl animate-float"
        style={{ animationDelay: '1.2s' }}
      />
      <div className="absolute left-1/2 top-1/3 h-64 w-64 -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(26,69,176,0.12) 1px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      />
    </div>
  )
}
